import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import React, { useImperativeHandle, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useImportedInvoicesStoreState } from "store/ImportedInvoicesStore";
import { ImportedInvoice, ImportedInvoiceEntry } from "types/importedInvoices";
import { formatDateWithTime } from "utils/pdf";
import {
  EntryColumnConfig,
  importedInvoiceEntriesColumnConfig,
} from "./importedInvoiceEntriesColumnConfig";
import { applyRedCellHighlights } from "utils/excelExportHighlights";

// Export of the visible invoices with one row per entry: the invoice columns are
// prepended to the entry columns of the detail grid.
const invoiceColumns: EntryColumnConfig[] = [
  { columnId: "invoiceNumber", width: 220, reorderable: false, resizable: true, sortable: false, label: "pct.importedInvoiceNumber.label", cellType: "customtext" },
  { columnId: "matchStateLabel", width: 130, reorderable: false, resizable: true, sortable: false, label: "pct.importedInvoiceMatchState.label", cellType: "customtext" },
  { columnId: "issuedDateFormatted", width: 100, reorderable: false, resizable: true, sortable: false, label: "pct.importedInvoiceIssuedDate.label", cellType: "customtext" },
  { columnId: "dueDateFormatted", width: 100, reorderable: false, resizable: true, sortable: false, label: "pct.dueDate.label", cellType: "customtext" },
  { columnId: "payerDisplayName", width: 200, reorderable: false, resizable: true, sortable: false, label: "pct.payerName.label", cellType: "customtext" },
  { columnId: "payerCode", width: 110, reorderable: false, resizable: true, sortable: false, label: "pct.importedInvoicePayerCode.label", cellType: "customtext" },
  { columnId: "invoicePremiumAmount", width: 100, reorderable: false, resizable: true, sortable: false, label: "pct.importedInvoicePremiumAmount.label", cellType: "customnumber" },
  { columnId: "paidAtSourceFormatted", width: 100, reorderable: false, resizable: true, sortable: false, label: "pct.importedInvoicePaidAtSource.label", cellType: "customtext" },
  { columnId: "pendingBankStatementEntryFormatted", width: 240, reorderable: false, resizable: true, sortable: false, label: "pct.importedInvoicePendingBankStatementEntry.label", cellType: "customtext" },
];

export type ExportExcelHandle = { save: () => void };

type Props = {};

export const ExportExcel = React.forwardRef<ExportExcelHandle, Props>(
  (props, ref) => {
    const { t } = useTranslation();
    const { importedInvoices, importedInvoiceMetadataMap } =
      useImportedInvoicesStoreState();
    const excelExportRef = useRef<ExcelExport>(null);

    const columns = useMemo(
      () => [
        ...invoiceColumns,
        ...importedInvoiceEntriesColumnConfig.filter(
          (c) => c.columnId !== "ordinalNumber" && c.columnId !== "options"
        ),
      ],
      []
    );

    // export exactly the invoices the grid shows, one row per entry
    const { data, entries } = useMemo(() => {
      const visibleInvoices = (importedInvoices ?? []).filter(
        (invoice: ImportedInvoice) =>
          importedInvoiceMetadataMap[invoice.importedInvoiceId]?.visible === true
      );
      const entries: ImportedInvoiceEntry[] = [];
      const data: Record<string, any>[] = [];
      visibleInvoices.forEach((invoice: ImportedInvoice) => {
        (invoice.importedInvoiceEntries ?? []).forEach((entry) => {
          entries.push(entry);
          data.push(
            columns.reduce((row: Record<string, any>, c) => {
              if (c.columnId === "invoicePremiumAmount") row[c.columnId] = invoice.premiumAmount;
              else if (invoiceColumns.some((ic) => ic.columnId === c.columnId)) row[c.columnId] = (invoice as any)[c.columnId];
              else row[c.columnId] = (entry as any)[c.columnId];
              return row;
            }, {})
          );
        });
      });
      return { data, entries };
    }, [columns, importedInvoices, importedInvoiceMetadataMap]);

    useImperativeHandle(
      ref,
      () => ({
        save: () => {
          const excelExport = excelExportRef.current;
          if (!excelExport) return;
          const workbook = excelExport.workbookOptions();
          applyRedCellHighlights(
            workbook,
            entries,
            columns.map((c) => c.additionalInfo?.redColorFn)
          );
          excelExport.save(workbook);
        },
      }),
      [entries, columns]
    );

    return (
      <ExcelExport
        data={data}
        ref={excelExportRef}
        fileName={`Imported Invoices ${formatDateWithTime(new Date())}.xlsx`}
      >
        {columns.map((col) => {
          const isNumber = col.cellType === "customnumber";
          return (
            <ExcelExportColumn
              key={col.columnId}
              field={col.columnId}
              title={t(col.label)}
              cellOptions={{ format: isNumber ? "#,##0.00" : "text" }}
              width={col.width}
              headerCellOptions={{
                background: "#005aa1",
                color: "#fff",
                textAlign: isNumber ? "right" : "left",
              }}
            />
          );
        })}
      </ExcelExport>
    );
  }
);
